import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { privateAxios } from "../axiosInstances";
import userPlacesQueryKeys from "../query-keys/places.query-keys";
import {
  FilterPlaceType,
  Paginated,
  PaginatedResponse,
  PlaceFilters,
  PlaceItemType,
  PlaceType,
  PlaceWithUserType,
  QueryArgs,
  Sortable,
} from "@ac-expo/api-types";

const getUserPlaces = async () => {
  const { data } = await privateAxios.get<PlaceType[]>("/places/mine");

  return data;
};

export const useUserPlacesQuery = () => {
  return useQuery({
    queryKey: userPlacesQueryKeys.me(),
    queryFn: () => getUserPlaces(),
  });
};

const getPlaces = async (filter?: FilterPlaceType) => {
  const { data } = await privateAxios.get<PlaceType[]>("/places", {
    params: filter,
  });

  return data;
};

export const usePlacesQuery = (filter?: FilterPlaceType | undefined) => {
  return useQuery({
    queryKey: userPlacesQueryKeys.lists(filter),
    queryFn: () => getPlaces(filter),
  });
};

const getPlace = async (id: string) => {
  const { data } = await privateAxios.get<PlaceType>(`/places/${id}`);
  return data;
};

export const usePlaceQuery = (id: string) => {
  return useQuery({
    queryKey: userPlacesQueryKeys.place(id),
    queryFn: () => getPlace(id),
    enabled: !!id,
  });
};

const getPlaceForAcExpo = async (id: string) => {
  const { data } = await privateAxios.get<PlaceWithUserType>(
    `/places/all/${id}`
  );
  return data;
};

export const usePlaceForAcExpoQuery = (id: string) => {
  return useQuery({
    queryKey: userPlacesQueryKeys.place(id),
    queryFn: () => getPlaceForAcExpo(id),
    enabled: !!id,
  });
};

const getPlacesItems = async (filter?: Paginated<FilterPlaceType>) => {
  const { data } = await privateAxios.get<PaginatedResponse<PlaceItemType>>(
    "/places/items",
    { params: filter }
  );

  return data;
};

export const useInfinitePlacesQuery = (
  filter?: FilterPlaceType | undefined
) => {
  return useInfiniteQuery({
    queryKey: userPlacesQueryKeys.lists(filter),
    queryFn: ({ pageParam }) =>
      getPlacesItems({ ...filter, pagination: pageParam }),
    getNextPageParam: (lastPage: PaginatedResponse<PlaceItemType>) => {
      if (!lastPage.pageSize || !lastPage.pageIndex) return undefined;

      return lastPage.data.length < lastPage.pageSize
        ? undefined
        : {
            pageIndex: lastPage.pageIndex + 1,
            pageSize: lastPage.pageSize,
          };
    },
    initialPageParam: {
      pageIndex: 0,
      pageSize: 10,
    },
  });
};

const getAllPlaces = async (
  filter?: Sortable<Paginated<FilterPlaceType>> | undefined
) => {
  const { data } = await privateAxios.get<PaginatedResponse<PlaceType>>(
    "/places/all",
    { params: filter }
  );

  return data;
};

export const useGetAllPlacesQuery = (
  filter?: Sortable<Paginated<PlaceFilters>> | undefined,
  options?: QueryArgs<PaginatedResponse<PlaceType>>
) => {
  return useQuery({
    queryKey: userPlacesQueryKeys.lists(filter),
    queryFn: () => getAllPlaces(filter),
    ...options,
  });
};

// const getPlacesAutoComplete = async (input: string) => {
//   const { data } = await privateAxios.get<PlaceType>("/places/auto-complete", {
//     params: { input },
//   });
//
//   return data;
// };
//
// export const useGetGoogleAutocomplete = (input: string) => {
//   // const debouncedInput = useDebounce(input, 500);
//
//   return useQuery({
//     queryKey: userPlacesQueryKeys.autoComplete(input),
//     queryFn: () => getPlacesAutoComplete(input),
//   });
// };
